import React, { useEffect } from 'react'
import { Box } from '@mantine/core'
import { IoAdd } from 'react-icons/io5'
import { Link, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { useStore } from '../stores/store'
import { AppIconButton, GunSlopWordmark } from '../common/ui'
import TopNavSettingsMenu from './TopNavSettingsMenu'
import navLogoUrl from '../icon-ak-sil/icon-styled.svg?url'

export const TOP_NAV_HEIGHT = 56

interface TopNavBarProps {
  onUploadClick?: () => void;
  onSettingsClick?: () => void;
}

export default observer(function TopNavBar ({ onUploadClick, onSettingsClick }: TopNavBarProps) {
  const { userStore, moderationStore } = useStore()
  const location = useLocation()
  const user = userStore.user

  // Must run before any conditional return — same hook order on /login and after redirect.
  useEffect(() => {
    if (!user?.isModerator && !user?.isAdmin) return
    void moderationStore.loadOpenFlagCount()
    const id = window.setInterval(() => void moderationStore.loadOpenFlagCount(), 120_000)
    return () => window.clearInterval(id)
  }, [user?.isModerator, user?.isAdmin, moderationStore])

  const hiddenRoutes = ['/login', '/register', '/forgot-password', '/reset-password']
  if (hiddenRoutes.some((route) => location.pathname.startsWith(route))) {
    return null
  }

  return (
    <Box
      component="header"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: TOP_NAV_HEIGHT,
        backgroundColor: 'var(--gs-bg-1)',
        borderBottom: '1px solid var(--gs-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 12,
        paddingRight: 12,
        zIndex: 101
      }}
    >
      {/* Left: + (fixed slot keeps logo centered) */}
      <Box sx={{ width: 44, display: 'flex', justifyContent: 'flex-start' }}>
        {userStore.isLoggedIn
          ? (
          <AppIconButton
            variant="filled"
            size={44}
            radius="xl"
            onClick={onUploadClick}
            sx={{
              backgroundColor: 'var(--gs-accent)',
              color: 'var(--gs-text-primary)',
              fontWeight: 800,
              '&:hover': { backgroundColor: 'var(--gs-accent-dark)' }
            }}
            aria-label="Upload"
          >
            <IoAdd size={26} strokeWidth={48} />
          </AppIconButton>
            )
          : (
          <Box sx={{ width: 44 }} />
            )}
      </Box>

      {/* Center: logo + wordmark */}
      <Box
        component={Link}
        to="/"
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          textDecoration: 'none',
          color: 'inherit'
        }}
      >
        <Box
          component="img"
          src={navLogoUrl}
          alt="GunSlop"
          sx={{
            width: 55,
            height: 55,
            borderRadius: 8,
            objectFit: 'contain',
            flexShrink: 0
          }}
          onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
            e.currentTarget.style.display = 'none'
          }}
        />
        <GunSlopWordmark
          size="md"
          sx={{ color: 'var(--gs-text-primary)' }}
        />
      </Box>

      {/* Right: settings menu */}
      <Box sx={{ width: 44, display: 'flex', justifyContent: 'flex-end' }}>
        <TopNavSettingsMenu
          user={user}
          openFlagCount={moderationStore.openFlagCount}
          onSettingsClick={onSettingsClick}
          onLogout={() => userStore.logout()}
        />
      </Box>
    </Box>
  )
})
